import { useForm } from 'react-hook-form';

import { Mail } from 'lucide-react';
import { z } from 'zod';

import { showPromiseToast } from '@/lib/toast';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useSubscribeToNotificationsMutation } from '@/store/api/serviceHealthNotificationSubscriptionApi';

import { zodResolver } from '@hookform/resolvers/zod';

const subscribeSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type SubscribeFormData = z.infer<typeof subscribeSchema>;

interface SubscribeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientShortName: string;
}

const SubscribeModal = ({ open, onOpenChange, clientShortName }: SubscribeModalProps) => {
  const [subscribeToNotifications] = useSubscribeToNotificationsMutation();

  const form = useForm<SubscribeFormData>({
    resolver: zodResolver(subscribeSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: SubscribeFormData) => {
    await showPromiseToast(
      subscribeToNotifications({
        email: data.email,
        client_short_name: clientShortName,
      }).unwrap(),
      {
        loading: 'Subscribing to status updates...',
        success: `Successfully subscribed! You will receive status updates at ${data.email}`,
        error: (error: any) =>
          error?.data?.detail || error?.message || 'Failed to subscribe to notifications',
      },
    );

    // Reset form and close modal
    form.reset();
    onOpenChange(false);
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 mb-2'>
            <Mail className='h-5 w-5' />
            Get status updates
          </DialogTitle>
          <DialogDescription>
            Subscribe to receive email notifications about service updates. Please be sure to add
            @mail.status.streamlyne.org to your allowed list of senders to avoid junk/spam.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4 py-4'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input placeholder='your.email@example.com' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex gap-2 justify-end'>
              <Button
                type='button'
                variant='outline'
                onClick={handleCancel}
                disabled={form.formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default SubscribeModal;
